import {ClockHoursTranscriptSection, ClockHoursTranscriptSectionCourse} from "types/clock-hours-transcript"
import styles from "./ProgressReportPdf.module.css"
import {chunk} from "lodash"
import {formatDecimal, formatDecimalWithoutRound} from "helpers"
import {Course} from "types/courses"

const COLUMNS_COUNT = 3
const EXAM_SUBMISSIONS_TYPE = "Exam"

type PracticalCourseForTranscript = {
    courseId: number
    totalQuantity?: number | string
    credits?: number | string
    submodules?: Array<{
        quantity: number
        units?: Course.Unit | string
        submittedSyncedCount?: number
        submittedCompletedCount?: number
        submittedCompletedHours?: number
        submissionsType?: string
    }>
}

type CoursePracticalStats = {
    result?: number
    min?: number
    rem?: number | string | null
}

type ClockHoursTranscriptSectionsProps = {
    data: ClockHoursTranscriptSection[]
    practicalCourses?: PracticalCourseForTranscript[]
}

const toNumber = (value: number | string | null | undefined): number | undefined => {
    if (value === null || value === undefined || value === "") {
        return undefined
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

/** Completed credit/hours from Practical submodule stats (uncapped — may exceed quantity). */
const formatQuantity = (value: number | string | null | undefined) => {
    if (typeof value === "number") {
        return formatDecimalWithoutRound(value)
    }
    return value ?? ""
}

/** Same aggregation as Practical CourseSection completed total (Credit count or Hour hours). */
const getCompletedQuantityForSubmodule = (submodule: {
    units?: Course.Unit | string
    submittedCompletedCount?: number
    submittedCompletedHours?: number
}): number => {
    return submodule.units === Course.Unit.Hour
        ? submodule.submittedCompletedHours || 0
        : submodule.submittedCompletedCount || 0
}

const getCoursePracticalStats = (
    course: ClockHoursTranscriptSectionCourse,
    practicalCoursesById: Record<number, PracticalCourseForTranscript>
): CoursePracticalStats => {
    if (!course?.courseId) {
        return {}
    }

    const practicalCourse = practicalCoursesById[course.courseId]
    const submodules = practicalCourse?.submodules ?? []
    const minQuantity = toNumber(practicalCourse?.totalQuantity ?? practicalCourse?.credits ?? course.credits)
    const isExam =
        Boolean(Number(course.isExam)) ||
        submodules.some((submodule) => submodule.submissionsType === EXAM_SUBMISSIONS_TYPE)
    const minimumPassingGrade = toNumber(course.minimumPassingGrade)

    let result: number | undefined
    if (isExam) {
        result = toNumber(course.examGrade)
    } else if (practicalCourse) {
        const completedTotal = submodules.reduce(
            (total, submodule) => total + getCompletedQuantityForSubmodule(submodule),
            0
        )
        if (completedTotal > 0) {
            result = completedTotal
        }
    }

    let rem: number | string | null = null
    if (isExam) {
        if (typeof result === "number" && typeof minimumPassingGrade === "number") {
            rem = result >= minimumPassingGrade ? "PASS" : "FAIL"
        }
    } else if (typeof minQuantity === "number") {
        rem = Math.max(minQuantity - (result ?? 0), 0)
    }

    return {
        result,
        // Exam Min = grading template minimumPassingGrade; non-exam Min = course requirements
        min: isExam ? minimumPassingGrade : minQuantity,
        rem
    }
}

const padIntoColumns = <T,>(items: T[], emptyItem: T): T[][] => {
    const padded = [...items]
    const perColumn = Math.max(1, Math.ceil(padded.length / COLUMNS_COUNT))
    const missing = perColumn * COLUMNS_COUNT - padded.length
    if (missing > 0) {
        padded.push(...Array.from({length: missing}, () => emptyItem))
    }
    return chunk(padded, perColumn)
}

const EMPTY_COURSE = {courseName: "", courseCode: "", credits: undefined} as ClockHoursTranscriptSectionCourse

export const ClockHoursTranscriptSections = ({data, practicalCourses = []}: ClockHoursTranscriptSectionsProps) => {
    if (!data?.length) {
        return null
    }

    const practicalCoursesById = practicalCourses.reduce<Record<number, PracticalCourseForTranscript>>(
        (acc, course) => {
            acc[course.courseId] = course
            return acc
        },
        {}
    )

    return (
        <div>
            {data.map((section) => {
                const courses = section.courses.filter((course) => course.courseName)
                const showSubmodules = courses.some((course) => course.submodules?.length)
                return (
                    <div className={styles.tableContainer} key={section.sectionId}>
                        <div className={styles.subjectBlock}>
                            <div style={{width: "50%"}}>
                                <span className={styles.subjectTitle}>Subject:</span>
                                <span className={styles.subjectValue}>{section.sectionTitle}</span>
                            </div>
                            <div>
                                <span className={styles.subjectTitle}>Grade:</span>
                                <span className={styles.subjectValue}>
                                    {typeof section.avgGpa === "number" ? formatDecimal(section.avgGpa) : "NA"}
                                </span>
                            </div>
                            <div>
                                <span className={styles.subjectTitle}>Completed:</span>
                                <span className={styles.subjectValue}>No</span>
                            </div>
                        </div>
                        {showSubmodules ? (
                            courses.map((course) => {
                                const {
                                    result,
                                    min: minQuantity,
                                    rem
                                } = getCoursePracticalStats(course, practicalCoursesById)
                                const submoduleColumns = padIntoColumns(course.submodules ?? [], {
                                    title: "",
                                    quantity: 0,
                                    submoduleId: 0
                                })
                                return (
                                    <div className={styles.courseBlock} key={course.sectionCourseId ?? course.courseId}>
                                        <div className={styles.courseSubject}>
                                            <span className={styles.courseSubjectName}>{course.courseName}</span>
                                            <span className={styles.courseSubjectStat}>
                                                Result: {formatQuantity(result)}
                                            </span>
                                            <span className={styles.courseSubjectStat}>
                                                Min: {typeof minQuantity === "number" ? minQuantity : ""}
                                            </span>
                                            <span className={styles.courseSubjectStat}>Rem: {formatQuantity(rem)}</span>
                                        </div>
                                        {course.submodules?.length ? (
                                            <div className={styles.tableBlockClockHoursTranscript}>
                                                {submoduleColumns.map((columnSubmodules, columnIndex) => (
                                                    <table
                                                        key={`${course.courseId}-${columnIndex}`}
                                                        className={styles.tableClockHoursTranscript}>
                                                        <thead>
                                                            <tr>
                                                                <th />
                                                                <th>Result</th>
                                                                <th>Min</th>
                                                                <th>Rem</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {columnSubmodules.map((submodule, submoduleIndex) => {
                                                                if (!submodule.title) {
                                                                    return (
                                                                        <tr
                                                                            key={`empty-${columnIndex}-${submoduleIndex}`}>
                                                                            <td />
                                                                            <td />
                                                                            <td />
                                                                            <td />
                                                                        </tr>
                                                                    )
                                                                }
                                                                const completed =
                                                                    getCompletedQuantityForSubmodule(submodule)
                                                                const submoduleMin = toNumber(submodule.quantity)
                                                                const submoduleRem =
                                                                    typeof submoduleMin === "number"
                                                                        ? Math.max(submoduleMin - completed, 0)
                                                                        : ""
                                                                return (
                                                                    <tr
                                                                        key={`${course.courseId}-${submodule.submoduleId}`}>
                                                                        <td>{submodule.title}</td>
                                                                        <td>{formatDecimalWithoutRound(completed)}</td>
                                                                        <td>
                                                                            {typeof submoduleMin === "number"
                                                                                ? submoduleMin
                                                                                : ""}
                                                                        </td>
                                                                        <td>{formatQuantity(submoduleRem)}</td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    </table>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            })
                        ) : (
                            <div className={styles.tableBlockClockHoursTranscript}>
                                {padIntoColumns(courses, EMPTY_COURSE).map((columnCourses, columnIndex) => (
                                    <table
                                        key={`${section.sectionId}-${columnIndex}`}
                                        className={styles.tableClockHoursTranscript}>
                                        <thead>
                                            <tr>
                                                <th />
                                                <th>Result</th>
                                                <th>Min</th>
                                                <th>Rem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {columnCourses.map((course, courseIndex) => {
                                                const {
                                                    result,
                                                    min: minQuantity,
                                                    rem
                                                } = getCoursePracticalStats(course, practicalCoursesById)
                                                return (
                                                    <tr key={course.courseId ?? `${columnIndex}-${courseIndex}`}>
                                                        <td>{course.courseName}</td>
                                                        <td>{formatQuantity(result)}</td>
                                                        <td>{typeof minQuantity === "number" ? minQuantity : ""}</td>
                                                        <td>{formatQuantity(rem)}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                ))}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
